import {
  Box,
  chakra,
  Flex,
  Image,
  Link,
  shouldForwardProp,
  Text,
  useColorMode,
  useColorModeValue
} from '@chakra-ui/react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { isValidMotionProp, motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { gameListFromBottom } from '../../../animateConstant';
import { COMMON_WEB_PATH } from '../../../constant';
import useSound from '../../../hook/useSound';
import { thisImageNeedDark } from '../../../utils/thisImageNeedDark';
import { routeMapping } from '../GameTypeSelector/utils/routeMapping';

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop)
});

const PlatformSelectorRight = ({ gameMainTypeFromProps, direction }) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const isHandleAllGame = useSelector((state) => state.isHandleAllGame);

  const boxBg = useColorModeValue('#E9E8F2', '#394367');
  const textColor = useColorModeValue('#757576', '#E9E8F2');

  const { gameList } = isHandleAllGame || {};

  const scrollRef = useRef();
  const splideRef = useRef();

  const { gameMainType, gamePlatform } = useParams(); // 獲取路由參數
  const playButtonSound = useSound();
  const hoverButtonSound = useSound({
    customSound: 'slide_sound',
    volume: 0.3
  });

  const getPlatform = useMemo(() => {
    const mainType = gameMainTypeFromProps || gameMainType;
    if (gameList) {
      // 通過routeMapping轉換遊戲類型
      const current_game_type = routeMapping(mainType)?.origin;
      return {
        // 獲取該遊戲類型下所有平台
        platform_list: Object?.keys(gameList[current_game_type] || {})
      };
    }
  }, [gameList, gameMainType, gameMainTypeFromProps]);

  const platformList = useMemo(() => {
    const mainType = gameMainTypeFromProps || gameMainType;
    const { platform_list } = getPlatform || {};

    return platform_list?.map((item) => {
      // 獲取每個平台的遊戲數量
      return (
        <SplideSlide key={item}>
          <Link
            as={NavLink}
            to={`/${mainType}/category/${item}/AllGames`}
            h={{
              base: '50px',
              md: '65px'
            }}
            _hover={{
              textDecoration: 'none'
            }}
            onMouseEnter={hoverButtonSound}
            onClick={playButtonSound}
          >
            {({ isActive }) => {
              return (
                <Box position={'relative'}>
                  <Box role="group">
                    <Flex
                      h={{
                        base: '80px',
                        md: '120px'
                      }}
                      w={{
                        base: '120px',
                        md: '220px'
                      }}
                      justifyContent={`center`}
                      gap="2.5px"
                      alignItems={`center`}
                      cursor="pointer"
                      borderRadius={{
                        base: '0px',
                        md: '15px'
                      }}
                      position={`relative`}
                      py="10px"
                      px={{
                        base: '10px',
                        md: '20px'
                      }}
                      bgSize="100%100%"
                      bgPos={'center'}
                      bgRepeat={`no-repeat`}
                      bgImage={`url('${COMMON_WEB_PATH}/blue-layout/gameType/platform_background.png')`}
                      _hover={{
                        content: '""',

                        bgImage: `url('${COMMON_WEB_PATH}/blue-layout/gameType/platform_background_active.png')`,
                        bgSize: '110%114%',
                        bgPos: 'center',
                        bgRepeat: 'no-repeat'
                      }}
                      _after={{
                        content: '""',
                        display: isActive ? 'block' : 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgImage: `url('${COMMON_WEB_PATH}/blue-layout/gameType/platform_background_active.png')`,
                        bgSize: '110%114%',
                        bgPos: 'center',
                        bgRepeat: 'no-repeat'
                      }}
                    >
                      <Image
                        w={{
                          base: '70%',
                          md: '60%'
                        }}
                        objectFit="cover"
                        position="relative"
                        zIndex="1"
                        src={`${COMMON_WEB_PATH}/ds88platformIcon/${thisImageNeedDark(
                          item.replace(/\s+/g, ''), // 移除所有空格
                          true
                        )}.png?1`}
                        alt={item}
                      />
                    </Flex>
                  </Box>
                </Box>
              );
            }}
          </Link>
        </SplideSlide>
      );
    });
  }, [getPlatform, gameList, gameMainTypeFromProps, boxBg, textColor]);

  useEffect(() => {
    const { platform_list } = getPlatform || {};

    if (splideRef.current) {
      const splide = splideRef.current.splide;

      splide.go(platform_list?.indexOf(gamePlatform));
    }
  }, [getPlatform, gamePlatform]);

  return (
    <>
      <Box
        ref={scrollRef}
        px={{
          xl: '8em'
        }}
        py={{
          base: '10px',
          sm: '0px'
        }}
        pt={{
          base: '10px'
        }}
        mx="auto"
        bg="rgba(42, 118, 156, 0.9)"
        borderTop="2px solid"
        borderBottom="2px solid"
        borderColor="rgba(42, 118, 156, 1)"
        position="relative"
      >
        <ChakraBox
          key={gameMainTypeFromProps || gameMainType}
          {...gameListFromBottom}
          position="relative"
          h="100%"
        >
          <Splide
            ref={splideRef}
            className="z-[3] !h-full"
            options={{
              wheel: true, // 啟用滾輪滾動
              pagination: false,
              arrows: false,
              drag: 'free',
              lazyLoad: 'nearby',
              trackClass: '!overflow-x-visible',
              gap: 15,
              breakpoints: {
                // 手機版配置 (768px 以下)
                768: {
                  fixedWidth: '120px',
                  fixedHeight: '80px',
                  gap: 8,
                  start: 10,
                  end: 10,
                  perPage: 3, // 手機版每次顯示3個
                  perMove: 1
                }
              },
              fixedWidth: '220px',
              fixedHeight: '120px', // 每個項目的固定高度
              height: '100%', // 根據方向設定高度
              dragMinThreshold: {
                mouse: 0,
                touch: 10
              },
              start: 20, // 開始位置的邊距
              end: 0 // 結束位置的邊距
            }}
          >
            {platformList}
          </Splide>
        </ChakraBox>
      </Box>
    </>
  );
};

export default PlatformSelectorRight;
